// Similar to ConversationV2.tsx
// Handles:
// - Detailed event view in sidebar/modal
// - Associated records display
// - Event metadata and payload display
// - Edit capabilities for certain event types
// - Related events timeline 

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  useTheme,
  alpha,
  Stack,
  Chip,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Zoom,
  Skeleton,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  DataObject as DataObjectIcon,
  AccessTime as AccessTimeIcon,
  Source as SourceIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  Person as PersonIcon,
  Forum as ForumIcon,
  Note as NoteIcon,
  Message as MessageIcon,
  Link as LinkIcon,
  Event as EventIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/store';
import { selectEventById, deleteEvent, setSelectedEvent } from 'src/features/timeline/events.slice';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import { useSnackbar } from 'notistack';
import Message from '../Channels/Message';
import ReactJson from 'react-json-view';
import { motion } from 'framer-motion';

interface EventDetailProps {
  eventId: string;
}

// Status configurations
const STATUS_CONFIG = {
  active: { icon: <CheckCircleIcon />, color: '#4CAF50', label: 'Active' },
  completed: { icon: <CheckCircleIcon />, color: '#2196F3', label: 'Completed' },
  error: { icon: <ErrorIcon />, color: '#f44336', label: 'Error' },
  pending: { icon: <PendingIcon />, color: '#FF9800', label: 'Pending' },
};

// Association type configs
const ASSOCIATION_CONFIG = {
  contacts: { icon: <PersonIcon />, color: '#E91E63', label: 'Contact' },
  conversations: { icon: <ForumIcon />, color: '#9C27B0', label: 'Conversation' },
  notes: { icon: <NoteIcon />, color: '#FF9800', label: 'Note' },
  messages: { icon: <MessageIcon />, color: '#2196F3', label: 'Message' },
};

const EventDetail: React.FC<EventDetailProps> = ({ eventId }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const { enqueueSnackbar } = useSnackbar();
  
  const event = useAppSelector(state => selectEventById(state, eventId));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonDialogContent, setJsonDialogContent] = useState<{ title: string; data: any } | null>(null);
  const menuOpen = Boolean(anchorEl);

  if (!event) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={200} />
        </Stack>
      </Box>
    );
  }

  const handleDelete = async () => {
    try {
      await dispatch(deleteEvent(event.id)).unwrap();
      dispatch(setSelectedEvent(null));
      enqueueSnackbar('Event deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete event', { variant: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  const handleCopyJSON = (data: any, label: string) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openJsonDialog = (title: string, data: any) => {
    setJsonDialogContent({ title, data });
    setJsonDialogOpen(true);
    handleMenuClose();
  };

  const statusConfig = STATUS_CONFIG[event.status as keyof typeof STATUS_CONFIG];

  // Get event icon based on type
  const getEventIcon = () => {
    const iconMap: { [key: string]: JSX.Element } = {
      message_sent: <MessageIcon />,
      message_received: <MessageIcon />,
      message_delivered: <MessageIcon />,
      note_created: <NoteIcon />,
      note_updated: <NoteIcon />,
      conversation_created: <ForumIcon />,
      conversation_status_changed: <ForumIcon />,
      contact_created: <PersonIcon />,
      contact_updated: <PersonIcon />,
      phone_call: <PhoneIcon />,
      email_sent: <EmailIcon />,
      email_received: <EmailIcon />,
      system: <CodeIcon />,
    };
    return iconMap[event.event_type] || <EventIcon />;
  };

  const renderAssociations = () => {
    if (!event.associations) return null;

    const sections = [];

    Object.entries(event.associations).forEach(([type, items]) => {
      if (!items || items.length === 0) return;

      const config = ASSOCIATION_CONFIG[type as keyof typeof ASSOCIATION_CONFIG];
      if (!config) return;

      sections.push(
        <motion.div
          key={type}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            sx={{
              mb: 2,
              borderRadius: 2,
              bgcolor: alpha(config.color, 0.05),
              border: `1px solid ${alpha(config.color, 0.2)}`,
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Box sx={{ color: config.color }}>{config.icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {config.label}{items.length > 1 ? 's' : ''} ({items.length})
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {items.map((item: any, index: number) => (
                  <Box
                    key={item.id || index}
                    sx={{
                      p: 1.5,
                      bgcolor: darkMode ? 'background.default' : 'grey.50',
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    {type === 'messages' && item.content && (
                      <Typography variant="body2">{item.content}</Typography>
                    )}
                    {type === 'contacts' && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email || item.phone_number || ''}
                        </Typography>
                      </Stack>
                    )}
                    {type === 'conversations' && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ForumIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {item.subject || `Conversation #${item.id?.slice(0, 8)}`}
                        </Typography>
                      </Stack>
                    )}
                    {type === 'notes' && (
                      <Typography variant="body2">
                        {item.subject || item.content || 'Note'}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      );
    });

    return sections;
  };

  return (
    <Fade in timeout={300}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: darkMode
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`
              : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#ffffff', 0.7)} 100%)`,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                {event.message || event.event_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Typography>
              
              {/* Metadata badges */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<AccessTimeIcon />}
                  label={dateTimeFormatter(event.created_at, 'full_with_time')}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiChip-icon': { fontSize: 16 },
                  }}
                />
                <Chip
                  icon={<SourceIcon />}
                  label={event.source}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    '& .MuiChip-icon': { fontSize: 16 },
                  }}
                />
                {statusConfig && (
                  <Chip
                    icon={statusConfig.icon}
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      bgcolor: alpha(statusConfig.color, 0.1),
                      color: statusConfig.color,
                      '& .MuiChip-icon': { 
                        fontSize: 16,
                        color: statusConfig.color,
                      },
                    }}
                  />
                )}
                <Chip
                  icon={getEventIcon()}
                  label={event.event_type}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    '& .MuiChip-icon': { fontSize: 16 },
                  }}
                />
              </Stack>
            </Box>
            
            {/* Actions */}
            <Stack direction="row" spacing={1}>
              <Tooltip title="More actions">
                <IconButton onClick={handleMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={() => dispatch(setSelectedEvent(null))}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Stack spacing={3}>
            {/* Description */}
            {event.description && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {event.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Associated Records */}
            {renderAssociations()}

            {/* Event Data */}
            <Stack spacing={2}>
              {event.payload && Object.keys(event.payload).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DataObjectIcon sx={{ color: theme.palette.info.main }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Payload
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            startIcon={<CopyIcon />}
                            onClick={() => handleCopyJSON(event.payload, 'Payload')}
                          >
                            Copy
                          </Button>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => openJsonDialog('Payload', event.payload)}
                          >
                            View
                          </Button>
                        </Stack>
                      </Stack>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: darkMode ? 'grey.900' : 'grey.50',
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        <ReactJson
                          src={event.payload}
                          theme={darkMode ? 'monokai' : 'rjv-default'}
                          collapsed={1}
                          displayDataTypes={false}
                          enableClipboard
                          style={{ backgroundColor: 'transparent' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {event.metadata && Object.keys(event.metadata).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DataObjectIcon sx={{ color: theme.palette.warning.main }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Metadata
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            startIcon={<CopyIcon />}
                            onClick={() => handleCopyJSON(event.metadata, 'Metadata')}
                          >
                            Copy
                          </Button>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => openJsonDialog('Metadata', event.metadata)}
                          >
                            View
                          </Button>
                        </Stack>
                      </Stack>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: darkMode ? 'grey.900' : 'grey.50',
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        <ReactJson
                          src={event.metadata}
                          theme={darkMode ? 'monokai' : 'rjv-default'}
                          collapsed={1}
                          displayDataTypes={false}
                          enableClipboard
                          style={{ backgroundColor: 'transparent' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </Stack>

            {/* Technical Details */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Technical Details
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Event ID</Typography>
                      <Typography variant="caption" sx={{ fontFamily: theme.typography.fontFamilyMonospace }}>{event.id}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Created At</Typography>
                      <Typography variant="caption">{dateTimeFormatter(event.created_at, 'iso')}</Typography>
                    </Stack>
                    {event.updated_at && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Updated At</Typography>
                        <Typography variant="caption">{dateTimeFormatter(event.updated_at, 'iso')}</Typography>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Stack>
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 8,
            sx: {
              minWidth: 180,
              borderRadius: 2,
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={() => console.log('Edit not implemented yet')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Event</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => console.log('Share not implemented yet')}>
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          TransitionComponent={Zoom}
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>Delete Event</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this event? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* JSON Viewer Dialog */}
        <Dialog
          open={jsonDialogOpen}
          onClose={() => setJsonDialogOpen(false)}
          maxWidth="md"
          fullWidth
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '80vh',
            },
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DataObjectIcon />
              <Typography variant="h6">{jsonDialogContent?.title}</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {jsonDialogContent && (
              <Box sx={{ 
                bgcolor: darkMode ? 'grey.900' : 'grey.50', 
                p: 2, 
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                maxHeight: '60vh',
                overflow: 'auto',
              }}>
                <ReactJson
                  src={jsonDialogContent.data}
                  theme={darkMode ? 'monokai' : 'rjv-default'}
                  collapsed={false}
                  displayDataTypes={false}
                  enableClipboard
                  style={{ backgroundColor: 'transparent' }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              startIcon={<CopyIcon />}
              onClick={() => jsonDialogContent && handleCopyJSON(jsonDialogContent.data, jsonDialogContent.title)}
            >
              Copy
            </Button>
            <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default EventDetail; 
